/**
 * Componente de configuración de FACe
 * Permite configurar el envío de facturas a Administraciones Públicas
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, XCircle, AlertCircle, Building2, Shield, Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "@/contexts/UserContext";

interface FaceConfig {
  id?: string;
  userId: string;
  habilitado: boolean;
  entorno: "pruebas" | "produccion";
  envioAutomatico: boolean;
  usuario?: string;
  endpointPersonalizado?: string;
  ultimoEnvio?: string;
  totalEnviadas: number;
  totalRechazadas: number;
  createdAt: string;
  updatedAt: string;
}

interface FaceComplianceStatus {
  habilitado: boolean;
  certificadoValido: boolean;
  configuracionCompleta: boolean;
  ultimoEnvioExitoso?: string;
  facturasPendientes: number;
  facturasFallidas: number;
  alertas: Array<{
    tipo: "error" | "warning" | "info";
    mensaje: string;
    timestamp: string;
  }>;
}

export function FaceConfig() {
  const { toast } = useToast();
  const { fetchWithAuth } = useUser();
  const [config, setConfig] = useState<FaceConfig | null>(null);
  const [status, setStatus] = useState<FaceComplianceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
    loadStatus();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetchWithAuth("/api/face/config");

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error("Error al cargar configuración:", error);
      toast({
        title: "Error",
        description: "No se pudo cargar la configuración de FACe",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const response = await fetchWithAuth("/api/face/status");

      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error("Error al cargar estado:", error);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    setSaving(true);
    try {
      const response = await fetchWithAuth("/api/face/config", {
        method: "PUT",
        body: JSON.stringify({
          habilitado: config.habilitado,
          entorno: config.entorno,
          envioAutomatico: config.envioAutomatico,
          usuario: config.usuario,
          endpointPersonalizado: config.endpointPersonalizado,
        }),
      });

      if (response.ok) {
        const updated = await response.json();
        setConfig(updated);
        await loadStatus();
        toast({
          title: "Configuración guardada",
          description: "Los cambios se han guardado correctamente",
        });
      } else {
        throw new Error("Error al guardar");
      }
    } catch (error) {
      console.error("Error al guardar:", error);
      toast({
        title: "Error",
        description: "No se pudo guardar la configuración",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!config) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>No se pudo cargar la configuración</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Estado de Cumplimiento */}
      {status && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Estado de Cumplimiento FACe
            </CardTitle>
            <CardDescription>
              Verifica que todo esté configurado correctamente para enviar facturas a Administraciones Públicas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex items-center gap-3">
                {status.habilitado ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-gray-400" />
                )}
                <div>
                  <p className="text-sm font-medium">FACe Habilitado</p>
                  <p className="text-xs text-muted-foreground">
                    {status.habilitado ? "Activo" : "Deshabilitado"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {status.certificadoValido ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <div>
                  <p className="text-sm font-medium">Certificado Digital</p>
                  <p className="text-xs text-muted-foreground">
                    {status.certificadoValido ? "Válido" : "No configurado"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {status.configuracionCompleta ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                )}
                <div>
                  <p className="text-sm font-medium">Configuración</p>
                  <p className="text-xs text-muted-foreground">
                    {status.configuracionCompleta ? "Completa" : "Pendiente"}
                  </p>
                </div>
              </div>
            </div>

            {/* Alertas */}
            {status.alertas.length > 0 && (
              <div className="space-y-2">
                {status.alertas.map((alerta, index) => (
                  <Alert
                    key={index}
                    variant={alerta.tipo === "error" ? "destructive" : "default"}
                  >
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{alerta.mensaje}</AlertDescription>
                  </Alert>
                ))}
              </div>
            )}

            {/* Estadísticas */}
            <div className="grid gap-4 md:grid-cols-3 pt-4 border-t">
              <div>
                <p className="text-2xl font-bold">{status.facturasPendientes}</p>
                <p className="text-xs text-muted-foreground">Facturas pendientes</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{config.totalEnviadas}</p>
                <p className="text-xs text-muted-foreground">Total enviadas</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{status.facturasFallidas}</p>
                <p className="text-xs text-muted-foreground">Envíos fallidos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configuración General */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configuración General
          </CardTitle>
          <CardDescription>
            Configura el envío automático de facturas a FACe (Factura Electrónica)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Habilitar FACe */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="habilitado">Habilitar envío a FACe</Label>
              <p className="text-sm text-muted-foreground">
                Activa el envío de facturas a Administraciones Públicas
              </p>
            </div>
            <Switch
              id="habilitado"
              checked={config.habilitado}
              onCheckedChange={(checked) =>
                setConfig({ ...config, habilitado: checked })
              }
            />
          </div>

          {/* Entorno */}
          <div className="space-y-2">
            <Label htmlFor="entorno">Entorno</Label>
            <Select
              value={config.entorno}
              onValueChange={(value: "pruebas" | "produccion") =>
                setConfig({ ...config, entorno: value })
              }
            >
              <SelectTrigger id="entorno">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pruebas">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Pruebas</Badge>
                    <span>Entorno de testing</span>
                  </div>
                </SelectItem>
                <SelectItem value="produccion">
                  <div className="flex items-center gap-2">
                    <Badge>Producción</Badge>
                    <span>Entorno real</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {config.entorno === "pruebas"
                ? "Las facturas se enviarán al entorno de pruebas de FACe"
                : "⚠️ Las facturas se enviarán al entorno de producción real"}
            </p>
          </div>

          {/* Envío Automático */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="envioAutomatico">Envío automático</Label>
              <p className="text-sm text-muted-foreground">
                Envía automáticamente facturas de AAPP al crearlas
              </p>
            </div>
            <Switch
              id="envioAutomatico"
              checked={config.envioAutomatico}
              onCheckedChange={(checked) =>
                setConfig({ ...config, envioAutomatico: checked })
              }
              disabled={!config.habilitado}
            />
          </div>

          {/* Usuario (opcional) */}
          <div className="space-y-2">
            <Label htmlFor="usuario">Usuario FACe (opcional)</Label>
            <Input
              id="usuario"
              type="text"
              placeholder="Si tu organización requiere usuario"
              value={config.usuario || ""}
              onChange={(e) =>
                setConfig({ ...config, usuario: e.target.value })
              }
              disabled={!config.habilitado}
            />
            <p className="text-xs text-muted-foreground">
              Normalmente no es necesario, se usa el certificado digital
            </p>
          </div>

          {/* Endpoint personalizado (avanzado) */}
          <div className="space-y-2">
            <Label htmlFor="endpoint">Endpoint personalizado (avanzado)</Label>
            <Input
              id="endpoint"
              type="url"
              placeholder="https://..."
              value={config.endpointPersonalizado || ""}
              onChange={(e) =>
                setConfig({ ...config, endpointPersonalizado: e.target.value })
              }
              disabled={!config.habilitado}
            />
            <p className="text-xs text-muted-foreground">
              Solo cambiar si tu organización usa un punto de acceso específico
            </p>
          </div>

          {/* Botón Guardar */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={loadConfig} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar configuración
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Información sobre FACe */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Sobre FACe
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>FACe</strong> (Factura Electrónica) es el Punto General de Entrada de
              Facturas Electrónicas de la Administración General del Estado. Es obligatorio
              para proveedores que facturan a Administraciones Públicas.
            </AlertDescription>
          </Alert>

          <div className="space-y-2 text-sm">
            <p>
              <strong>Requisitos:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Certificado digital válido (persona física o jurídica)</li>
              <li>Facturas en formato Facturae 3.2.2 firmadas digitalmente</li>
              <li>Códigos DIR3 correctos del organismo destinatario</li>
              <li>
                Los clientes deben tener marcado "Es Administración Pública" y los 3 códigos DIR3
              </li>
            </ul>
          </div>

          <div className="pt-4">
            <p className="text-xs text-muted-foreground">
              📚 Más información:{" "}
              <a
                href="https://www.facturae.gob.es/face/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                www.facturae.gob.es/face
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
